import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, CloudOff } from "lucide-react";
import { api, API } from "@/lib/api";

const SAVE_EVERY_MS = 30_000;
const localKey = (token) => `rs_code_${token}`;

export const readLocalDraft = (token) => {
  try { return JSON.parse(localStorage.getItem(localKey(token)) || "null"); } catch (_) { return null; }
};

// Pick whichever saved draft (server vs this browser) is newer.
export const newestDraft = (server, local) => {
  if (!server) return local;
  if (!local) return server;
  return new Date(local.saved_at) > new Date(server.saved_at) ? local : server;
};

// Autosave: local copy on every change, server copy every 30s while dirty (plus on tab hide).
export function useCodeAutosave(token, enabled) {
  const drafts = useRef({});
  const language = useRef("python");
  const dirty = useRef(false);
  const [status, setStatus] = useState({ state: "idle", at: null });

  const flush = useCallback(async () => {
    if (!dirty.current || !enabled) return;
    dirty.current = false;
    setStatus(s => ({ ...s, state: "saving" }));
    try {
      const { data } = await api.post(`/coding/${token}/draft`, { drafts: drafts.current, language: language.current });
      setStatus({ state: "saved", at: data.saved_at });
    } catch (e) {
      if (e.response?.status !== 409) dirty.current = true;
      setStatus(s => ({ ...s, state: e.response ? "error" : "offline" }));
    }
  }, [token, enabled]);

  const record = useCallback((lang, code) => {
    drafts.current = { ...drafts.current, [lang]: code };
    language.current = lang;
    dirty.current = true;
    try { localStorage.setItem(localKey(token), JSON.stringify({ drafts: drafts.current, language: lang, saved_at: new Date().toISOString() })); } catch (_) {}
  }, [token]);

  const seed = useCallback((d) => { drafts.current = { ...(d?.drafts || {}) }; if (d?.language) language.current = d.language; }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const t = setInterval(flush, SAVE_EVERY_MS);
    const onHide = () => {
      if (document.visibilityState !== "hidden" || !dirty.current) return;
      dirty.current = false;
      fetch(`${API}/coding/${token}/draft`, {
        method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ drafts: drafts.current, language: language.current }),
      }).catch(() => { dirty.current = true; });
    };
    const onOnline = () => flush();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("online", onOnline);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onHide); window.removeEventListener("online", onOnline); };
  }, [enabled, flush, token]);

  return { status, record, seed, flush, drafts };
}

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export function AutosaveBadge({ status }) {
  const { state, at } = status;
  if (state === "idle") return null;
  const map = {
    saving: [<Loader2 key="i" className="w-3 h-3 animate-spin" />, "Saving…", "text-slate-400"],
    saved: [<Check key="i" className="w-3 h-3" />, `Saved ${at ? fmtTime(at) : ""}`, "text-emerald-400"],
    offline: [<CloudOff key="i" className="w-3 h-3" />, "Offline — saved locally", "text-amber-400"],
    error: [<CloudOff key="i" className="w-3 h-3" />, "Not saved — saved locally", "text-amber-400"],
  };
  const [icon, text, tone] = map[state];
  return <span data-testid="autosave-status" data-state={state} className={`hidden sm:inline-flex items-center gap-1 text-[11px] ${tone}`}>{icon}{text}</span>;
}
